/**
 * Human-readable cadence labels for the crons we surface in the admin UI.
 *
 * Source of truth for the actual cron expressions lives in
 * `apps/cloud-tasks/src/index.ts` — this file mirrors those for display
 * only. The parity test `cronSchedules.test.ts` asserts every
 * `WATCHED_CRONS` entry has a matching entry here; it cannot verify the
 * label text itself against the backend, so keep them in sync manually
 * when a schedule changes.
 *
 * `cadenceMinutes` is set only for interval-style "every N minutes"
 * cron patterns whose next tick aligns to wall-clock slots of N minutes.
 * Daily crons (e.g. `30 2 * * *`) leave it undefined — `nextTickHint`
 * then returns null and the UI simply omits the countdown.
 */

export interface CronSchedule {
  label: string;
  cadenceMinutes?: number;
}

export const CRON_SCHEDULES: Record<string, CronSchedule> = {
  IntentOrchestratorCron: { label: 'every 5 min', cadenceMinutes: 5 },
  HealthMetricsRollupCron: { label: 'every 15 min', cadenceMinutes: 15 },
  EngagementReconcilerCron: { label: 'every 15 min', cadenceMinutes: 15 },
  EngagementSignalRefreshCron: { label: 'daily at 02:30 UTC' },
  EngagementWindowRefreshCron: { label: 'daily at 04:00 UTC' },
  IntentExpirationSweepCron: { label: 'daily at 03:00 UTC' },
  IntentRetentionSweepCron: { label: 'daily at 03:15 UTC' },
  AlertEvaluatorCron: { label: 'every 5 min', cadenceMinutes: 5 },
};

/**
 * Minutes until the next wall-clock slot for an interval cron. Cloud
 * Scheduler fires on absolute minute boundaries (for "every 5 min",
 * :00, :05, :10, …), so we compute ceil(now / cadence) * cadence rather
 * than cadence - elapsedSinceLastStart — the latter can be off by nearly
 * a full cadence window when the last run finishes far from a slot edge.
 */
function minutesUntilNextWallClockSlot(cadenceMinutes: number): number {
  const now = new Date();
  const minutesIntoHour =
    now.getMinutes() + now.getSeconds() / 60 + now.getMilliseconds() / 60_000;
  let nextSlot = Math.ceil(minutesIntoHour / cadenceMinutes) * cadenceMinutes;
  // If we are exactly on a slot boundary, advance to the next one.
  if (nextSlot === minutesIntoHour) nextSlot += cadenceMinutes;
  return Math.max(0, nextSlot - minutesIntoHour);
}

/**
 * Short "next tick" hint for an interval cron, computed from wall-clock
 * alignment (not elapsed-since-last-heartbeat). Returns null when the
 * schedule is not interval-based (e.g. daily crons).
 */
export function nextTickHint(cadenceMinutes: number | undefined): string | null {
  if (!cadenceMinutes) return null;
  const minutesUntil = minutesUntilNextWallClockSlot(cadenceMinutes);
  if (minutesUntil < 1) return 'next tick any moment';
  return `next in ~${Math.round(minutesUntil)}m`;
}
